import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Switch,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import type { Protocol, AuthType, CreateOnPremiseDestinationRequest } from '@/types/backup-destination.types';
import { fieldErrorsFromApi, useFieldErrors, type FieldErrors } from '@/utils/formErrors';

// The inputs inside 進階設定; an error on one of them opens the accordion so
// the hint is not hidden (QA #68).
const ADVANCED_FIELDS = ['connection_timeout', 'transfer_timeout', 'retry_attempts'];

const inRange = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

interface OnPremiseConfigFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOnPremiseDestinationRequest) => Promise<void>;
  initialData?: Partial<CreateOnPremiseDestinationRequest>;
  isEdit?: boolean;
}

const defaultPorts: Record<Protocol, number> = {
  sftp: 22,
  smb: 445,
};

export default function OnPremiseConfigForm({
  open,
  onClose,
  onSubmit,
  initialData,
  isEdit = false,
}: OnPremiseConfigFormProps) {
  const [loading, setLoading] = useState(false);
  // `error` is only for problems that belong to no field (unreachable API …);
  // field problems — own checks and the backend's 400 VALIDATION_ERROR
  // fields, keyed by the request's JSON keys — are marked on the input.
  const [error, setError] = useState<string | null>(null);
  const form = useFieldErrors();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  // Form state
  const [displayName, setDisplayName] = useState(initialData?.display_name || '');
  const [protocol, setProtocol] = useState<Protocol>(initialData?.protocol || 'sftp');
  const [host, setHost] = useState(initialData?.host || '');
  const [port, setPort] = useState(initialData?.port || defaultPorts.sftp);
  const [basePath, setBasePath] = useState(initialData?.base_path || '/backup');
  const [authType, setAuthType] = useState<AuthType>(initialData?.auth_type || 'password');
  const [username, setUsername] = useState(initialData?.username || '');
  const [password, setPassword] = useState(initialData?.password || '');
  const [privateKey, setPrivateKey] = useState(initialData?.private_key || '');
  const [privateKeyPassphrase, setPrivateKeyPassphrase] = useState(initialData?.private_key_passphrase || '');
  const [domain, setDomain] = useState(initialData?.domain || '');
  const [useTls, setUseTls] = useState(initialData?.use_tls ?? true);
  const [verifyHost, setVerifyHost] = useState(initialData?.verify_host ?? true);
  const [connectionTimeout, setConnectionTimeout] = useState(initialData?.connection_timeout || 30);
  const [transferTimeout, setTransferTimeout] = useState(initialData?.transfer_timeout || 3600);
  const [retryAttempts, setRetryAttempts] = useState(initialData?.retry_attempts || 3);

  const handleProtocolChange = (newProtocol: Protocol) => {
    setProtocol(newProtocol);
    setPort(defaultPorts[newProtocol]);
    // Reset auth type for SMB (only password supported)
    if (newProtocol === 'smb') {
      setAuthType('password');
    }
  };

  // Every problem is marked on its own field at once (QA #68); the wording
  // mirrors the backend's binding rules (min / max, 1–65535).
  const validate = (): FieldErrors => {
    const problems: FieldErrors = {};
    if (!displayName.trim()) problems.display_name = '請輸入這個備份目的地的名稱（例如：公司 NAS 備份）';
    if (!host.trim()) problems.host = '請輸入主機位址（IP 或主機名稱，例如 192.168.1.100）';
    if (!inRange(port, 1, 65535)) problems.port = '請輸入 1–65535 之間的埠號';
    if (!basePath.trim()) {
      problems.base_path =
        protocol === 'smb' ? '請輸入備份路徑（Windows 路徑格式，例如 \\\\share\\backup）' : '請輸入備份路徑（Unix 路徑格式，例如 /backup/sentrax）';
    }
    if (!username.trim()) problems.username = '請輸入登入這台主機用的使用者名稱';
    if (authType === 'password' && !password && !isEdit) problems.password = '請輸入登入這台主機用的密碼';
    if (authType === 'key' && !privateKey && !isEdit) {
      problems.private_key = '請貼上 SSH 私鑰的完整內容（以 -----BEGIN 開頭、-----END 結尾）';
    }
    if (!inRange(connectionTimeout, 1, 300)) problems.connection_timeout = '請輸入 1–300 之間的秒數';
    if (!inRange(transferTimeout, 60, 86400)) problems.transfer_timeout = '請輸入 60–86400 之間的秒數';
    if (!inRange(retryAttempts, 0, 10)) problems.retry_attempts = '請輸入 0–10 之間的次數';
    return problems;
  };

  const showFieldProblems = (problems: FieldErrors) => {
    if (ADVANCED_FIELDS.some((f) => problems[f])) setAdvancedOpen(true);
    form.setErrors(problems);
  };

  const handleSubmit = async () => {
    setError(null);

    const problems = validate();
    if (Object.keys(problems).length > 0) {
      showFieldProblems(problems);
      return;
    }
    form.clear();

    const data: CreateOnPremiseDestinationRequest = {
      display_name: displayName.trim(),
      protocol,
      host: host.trim(),
      port,
      base_path: basePath.trim(),
      auth_type: authType,
      username: username.trim(),
      password: authType === 'password' ? password : undefined,
      private_key: authType === 'key' ? privateKey : undefined,
      private_key_passphrase: authType === 'key' && privateKeyPassphrase ? privateKeyPassphrase : undefined,
      domain: protocol === 'smb' && domain ? domain.trim() : undefined,
      use_tls: useTls,
      verify_host: verifyHost,
      connection_timeout: connectionTimeout,
      transfer_timeout: transferTimeout,
      retry_attempts: retryAttempts,
    };

    setLoading(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (err) {
      // 400 VALIDATION_ERROR fields land on the inputs (QA #69); the rest
      // stays in the Alert above the form.
      if (ADVANCED_FIELDS.some((f) => fieldErrorsFromApi(err).fields[f])) setAdvancedOpen(true);
      setError(form.apply(err, {}, '儲存失敗，請稍後再試') || null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? '編輯地端主機' : '新增地端主機'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Basic Info */}
          <TextField
            label="名稱"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            required
            placeholder="例如：公司 NAS 備份"
            {...form.bind('display_name')}
          />

          <FormControl fullWidth error={Boolean(form.errors.protocol)}>
            <InputLabel>傳輸協定</InputLabel>
            <Select
              name="protocol"
              value={protocol}
              label="傳輸協定"
              onChange={(e) => {
                handleProtocolChange(e.target.value as Protocol);
                form.clearField('protocol');
              }}
              // PUT /backup-destinations/:id has no protocol field: an
              // SFTP↔SMB change would half-apply (port / path update, protocol
              // does not). Fixed at creation; delete and recreate to change it.
              disabled={isEdit}
            >
              <MenuItem value="sftp">SFTP (SSH File Transfer Protocol)</MenuItem>
              <MenuItem value="smb">SMB (Windows 網路共享)</MenuItem>
            </Select>
            {form.errors.protocol && <FormHelperText>{form.errors.protocol}</FormHelperText>}
          </FormControl>

          {/* Connection Info */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="主機位址"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              fullWidth
              required
              placeholder="例如：192.168.1.100 或 nas.company.local"
              {...form.bind('host')}
            />
            <TextField
              label="連接埠"
              type="number"
              value={port}
              onChange={(e) => setPort(parseInt(e.target.value) || defaultPorts[protocol])}
              sx={{ width: 160 }}
              InputProps={{
                inputProps: { min: 1, max: 65535 },
              }}
              {...form.bind('port')}
            />
          </Box>

          <TextField
            label="備份路徑"
            value={basePath}
            onChange={(e) => setBasePath(e.target.value)}
            fullWidth
            required
            placeholder={protocol === 'smb' ? '例如：\\\\share\\backup' : '例如：/backup/sentrax'}
            {...form.bind('base_path', protocol === 'smb' ? '請使用 Windows 路徑格式' : '請使用 Unix 路徑格式')}
          />

          {/* Authentication */}
          <FormControl component="fieldset">
            <FormLabel component="legend">認證方式</FormLabel>
            <RadioGroup
              row
              value={authType}
              onChange={(e) => setAuthType(e.target.value as AuthType)}
            >
              <FormControlLabel value="password" control={<Radio />} label="密碼認證" />
              {protocol === 'sftp' && (
                <FormControlLabel value="key" control={<Radio />} label="SSH 金鑰認證" />
              )}
            </RadioGroup>
          </FormControl>

          <TextField
            label="使用者名稱"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            required
            {...form.bind('username')}
          />

          {protocol === 'smb' && (
            <TextField
              label="網域 (選填)"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              fullWidth
              placeholder="例如：COMPANY"
              {...form.bind('domain', 'Windows 網域名稱，若無則留空')}
            />
          )}

          {authType === 'password' && (
            <TextField
              label="密碼"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required={!isEdit}
              {...form.bind('password', isEdit ? '若不修改密碼請留空' : undefined)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {authType === 'key' && (
            <>
              <TextField
                label="SSH 私鑰"
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                fullWidth
                required={!isEdit}
                multiline
                rows={4}
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                {...form.bind('private_key', isEdit ? '若不修改私鑰請留空' : '貼上您的 SSH 私鑰內容')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPrivateKey(!showPrivateKey)}
                        edge="end"
                      >
                        {showPrivateKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                type={showPrivateKey ? 'text' : 'password'}
              />
              <TextField
                label="私鑰密碼 (選填)"
                type="password"
                value={privateKeyPassphrase}
                onChange={(e) => setPrivateKeyPassphrase(e.target.value)}
                fullWidth
                {...form.bind('private_key_passphrase', '若私鑰有設定密碼，請在此輸入')}
              />
            </>
          )}

          {/* Advanced Settings */}
          <Accordion expanded={advancedOpen} onChange={(_e, open) => setAdvancedOpen(open)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>進階設定</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {protocol === 'sftp' && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useTls}
                          onChange={(e) => setUseTls(e.target.checked)}
                        />
                      }
                      label="使用 TLS/SSL 加密"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={verifyHost}
                          onChange={(e) => setVerifyHost(e.target.checked)}
                        />
                      }
                      label="驗證主機金鑰"
                    />
                  </>
                )}

                <TextField
                  label="連線超時 (秒)"
                  type="number"
                  value={connectionTimeout}
                  onChange={(e) => setConnectionTimeout(parseInt(e.target.value) || 30)}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 1, max: 300 },
                  }}
                  {...form.bind('connection_timeout', '1–300 秒')}
                />

                <TextField
                  label="傳輸超時 (秒)"
                  type="number"
                  value={transferTimeout}
                  onChange={(e) => setTransferTimeout(parseInt(e.target.value) || 3600)}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 60, max: 86400 },
                  }}
                  {...form.bind('transfer_timeout', '單一檔案傳輸的最大等待時間（60–86400 秒）')}
                />

                <TextField
                  label="重試次數"
                  type="number"
                  value={retryAttempts}
                  onChange={(e) => setRetryAttempts(parseInt(e.target.value) || 3)}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, max: 10 },
                  }}
                  {...form.bind('retry_attempts', '0–10 次')}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? '儲存中...' : '儲存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
